import type { Placement } from '@popperjs/core';
import type { Options } from '@popperjs/core/lib/modifiers/offset';
import { Size } from '@ps/web-core-enums';
import { select } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import classNames from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { useSelector } from 'react-redux';

import Info from '../../assets/icons/info.svg';
import Translation from '../Translation/Translation';
import useScreenSize from '../hooks/useScreenSize/useScreenSize';

import styles from './InfoTooltip.module.scss';

export interface Props {
  content: React.ComponentProps<typeof Translation>[] | React.ReactNode;
  addHoverEffect?: boolean;
  arrowClassName?: string;
  iconClassName?: string;
  tagName?: React.ElementType;
  tooltipClassName?: string;
  tooltipContentWrapperClassName?: string;
  offset?: Options['offset'];
  onClick?(): void;
  placement?: Placement;
}

type TooltipElement = HTMLDivElement | null;

const InfoTooltip: React.FC<React.PropsWithChildren<Props>> & { styles: typeof styles } = ({
  children,
  content,
  addHoverEffect = false,
  arrowClassName,
  iconClassName,
  tagName = 'p',
  tooltipClassName,
  tooltipContentWrapperClassName,
  offset,
  onClick,
  placement
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [referenceElement, setReferenceElement] = useState<TooltipElement>(null);
  const [popperElement, setPopperElement] = useState<TooltipElement>(null);
  const [arrowElement, setArrowElement] = useState<TooltipElement>(null);
  const isMobile = useSelector(select.device.isMobile);
  const { greaterThan } = useScreenSize();

  const { styles: tooltipStyles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'offset',
        options: { offset: offset || [-120, greaterThan(Size.Small) ? 15 : 5] }
      },
      {
        name: 'flip',
        options: { fallbackPlacements: ['top', 'bottom'] }
      }
    ]
  });

  const closeTooltip = useCallback(
    (event: TouchEvent | MouseEvent) => {
      if (!showTooltip) {
        return;
      }

      if (
        !popperElement?.contains(event.target as Node) &&
        !referenceElement?.contains(event.target as Node)
      ) {
        setShowTooltip(false);
      }
    },
    [showTooltip, popperElement, referenceElement]
  );

  useEffect(() => {
    if (canUseDOM) {
      if (isMobile) {
        window.addEventListener('touchend', closeTooltip, false);
      } else {
        window.addEventListener('click', closeTooltip, false);
      }
    }

    return () => {
      if (canUseDOM) {
        if (isMobile) {
          window.removeEventListener('touchend', closeTooltip, false);
        } else {
          window.removeEventListener('click', closeTooltip, false);
        }
      }
    };
  }, [showTooltip, closeTooltip, isMobile]);

  return (
    <>
      <div
        ref={setReferenceElement}
        onMouseLeave={() => addHoverEffect && setShowTooltip(false)}
        onMouseOver={() => addHoverEffect && setShowTooltip(true)}
        onClick={() => {
          onClick?.();
          setShowTooltip(!showTooltip);
        }}
      >
        {children ? (
          <span>{children}</span>
        ) : (
          <span className={classNames(styles.info, { [styles.show]: showTooltip }, iconClassName)}>
            <Info className={styles.infoIcon} />
          </span>
        )}
      </div>
      {showTooltip && (
        <div
          className={classNames(styles.tooltip, tooltipClassName)}
          ref={setPopperElement}
          style={tooltipStyles.popper}
          {...attributes.popper}
        >
          <div className={tooltipContentWrapperClassName}>
            {Array.isArray(content)
              ? content.map((tip, i) => (
                  <React.Fragment key={i}>
                    <Translation tagName={tagName} {...tip} />
                  </React.Fragment>
                ))
              : content}
          </div>
          <div
            className={classNames(styles.tooltipArrow, arrowClassName)}
            ref={setArrowElement}
            style={tooltipStyles.arrow}
          />
        </div>
      )}
    </>
  );
};

InfoTooltip.styles = styles;

export default InfoTooltip;
